import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { fetchDDODashboard, DDODashboardStats } from "@/api/ddoApi";
import { formatWorkflowStatusLabel } from "@/utils/semanticLabels";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
type QueueQuickFilter = {
  label: string;
  params?: Record<string, string | boolean | undefined>;
  value: number;
  help?: string;
};

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
function toYMD(d: Date) {
  return format(d, "yyyy-MM-dd");
}

function toYMDOptional(d: Date | null) {
  return d ? format(d, "yyyy-MM-dd") : undefined;
}

function parseYMD(s: string | null): Date | null {
  if (!s) return null;
  const parsed = new Date(s);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function buildQuery(params: Record<string, string | boolean | undefined>) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      sp.set(k, String(v));
    }
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

// -----------------------------------------------------------------------------
// UI Components
// -----------------------------------------------------------------------------
const ClickableStatCard: React.FC<{
  label: string;
  value: number;
  onClick: () => void;
  help?: string;
}> = ({ label, value, onClick, help }) => {
  return (
    <button type="button" onClick={onClick} className="text-left w-full">
      <Card className="shadow-sm hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <div className="text-3xl font-bold text-gray-900">{value}</div>
          {help && <div className="text-xs text-muted-foreground">{help}</div>}
          <div className="text-xs text-indigo-600">Open worklist →</div>
        </CardContent>
      </Card>
    </button>
  );
};

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------
const DDODashboard: React.FC = () => {
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  const [stats, setStats] = useState<DDODashboardStats | null>(null);
  const [error, setError] = useState<string>("");

  const inheritedStart = sessionStorage.getItem("operations_date_from");
  const inheritedEnd = sessionStorage.getItem("operations_date_to");
  const inheritedProgram = sessionStorage.getItem("operations_program_code") || "";
  const inheritedAcademicYear = sessionStorage.getItem("operations_academic_year") || "";
  const inheritedApplicantName = sessionStorage.getItem("operations_applicant_name") || "";

  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: parseYMD(inheritedStart),
    endDate: parseYMD(inheritedEnd),
  });
  const [programFilter, setProgramFilter] = useState(inheritedProgram);
  const [academicYearFilter, setAcademicYearFilter] = useState(inheritedAcademicYear);
  const [applicantNameFilter, setApplicantNameFilter] = useState(inheritedApplicantName);

  // ---------------------------------------------------------------------------
  // Canonical tenant resolution (URL → storage → default)
  // ---------------------------------------------------------------------------
  const reserved = useMemo(
    () => new Set(["ddo", "pm", "pc", "finance", "management", "login", "403"]),
    []
  );

  const paramSlug =
    tenantSlug && tenantSlug.trim() && !reserved.has(tenantSlug.trim())
      ? tenantSlug.trim()
      : null;

  const storedSlugRaw = sessionStorage.getItem("tenant_slug");
  const storedSlug =
    storedSlugRaw && storedSlugRaw.trim() && !reserved.has(storedSlugRaw.trim())
      ? storedSlugRaw.trim()
      : null;

  const slug = paramSlug ?? storedSlug ?? "default";

  // ---------------------------------------------------------------------------
  // Data fetch
  // ---------------------------------------------------------------------------
  const fetchStats = async () => {
    try {
      setError("");
      setStats(null);

      const data = await fetchDDODashboard({
        start_date: toYMDOptional(dateRange.startDate),
        end_date: toYMDOptional(dateRange.endDate),
        program_code: programFilter.trim() || undefined,
        academic_year: academicYearFilter.trim()
          ? Number(academicYearFilter.trim())
          : undefined,
        applicant_name: applicantNameFilter.trim() || undefined,
      });

      setStats(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load dashboard data.");
    }
  };

  useEffect(() => {
    void fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRange.startDate,
    dateRange.endDate,
    programFilter,
    academicYearFilter,
    applicantNameFilter,
  ]);

  // ---------------------------------------------------------------------------
  // Empty-state detection (truthful)
  // ---------------------------------------------------------------------------
  const isTrulyEmpty =
    !!stats &&
    stats.assignedToMe === 0 &&
    stats.awaitingMyAction === 0 &&
    stats.upcomingInterviews === 0 &&
    stats.mentorshipLogs === 0 &&
    (!stats.statusBreakdown || Object.keys(stats.statusBreakdown).length === 0);

  const quickFilters: QueueQuickFilter[] = useMemo(() => {
    const start_date = toYMDOptional(dateRange.startDate);
    const end_date = toYMDOptional(dateRange.endDate);
    const sharedParams = {
      date_from: start_date,
      date_to: end_date,
      program_code: programFilter.trim() || undefined,
      academic_year: academicYearFilter.trim() || undefined,
      applicant_name: applicantNameFilter.trim() || undefined,
    };
    const statusTiles = Object.entries(stats?.statusBreakdown ?? {})
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([status, count]) => ({
        label: formatWorkflowStatusLabel(status),
        value: count,
        params: { ...sharedParams, status, include_locked: true },
        help: "Applications with this status",
      }));

    return [
      {
        label: "Awaiting my action",
        value: stats?.awaitingMyAction ?? 0,
        params: { ...sharedParams, actionable_only: true },
        help: "Assigned applications with at least one available review action",
      },
      {
        label: "My Total Applications",
        value: stats?.myTotalApplications ?? stats?.assignedToMe ?? 0,
        params: { ...sharedParams, include_locked: true },
        help: "Total applications assigned to you in this tenant scope",
      },
      {
        label: "Stale Assignments",
        value: stats?.staleAssignments ?? 0,
        params: { ...sharedParams, stale_only: true },
        help: "Actionable assignments older than 48 hours",
      },
      {
        label: "Locked Applications",
        value: stats?.lockedApplications ?? 0,
        params: { ...sharedParams, locked_only: true, include_locked: true },
        help: "Applications waiting outside DDO control or explicitly locked",
      },
      {
        label: "Total Applications Disbursed",
        value: stats?.totalApplicationsDisbursed ?? 0,
        params: { ...sharedParams, status: "disbursed", include_locked: true },
        help: "Assigned applications that reached payment",
      },
      ...statusTiles,
    ];
  }, [
    stats,
    dateRange.startDate,
    dateRange.endDate,
    programFilter,
    academicYearFilter,
    applicantNameFilter,
  ]);


  const openQueue = (params?: Record<string, string | boolean | undefined>) => {
    const qs = buildQuery(params || {});
    navigate(`/${slug}/ddo/submissions${qs}`);
  };

  const nextAction = stats
    ? stats.awaitingMyAction > 0
      ? "Review assigned applications waiting for your action."
      : stats.staleAssignments > 0
        ? "Open applications waiting too long and review what is blocking them."
        : "No DDO review action is needed right now."
    : "Load your review worklist.";

  // ---------------------------------------------------------------------------
  // Export
  // ---------------------------------------------------------------------------
  const exportCSV = () => {
    if (!stats) return;

    const rows: Array<Array<string>> = [
      ["Metric", "Value"],
      ["Awaiting my action", String(stats.awaitingMyAction ?? 0)],
      ["My Total Applications", String(stats.myTotalApplications ?? stats.assignedToMe ?? 0)],
      ["Stale Assignments", String(stats.staleAssignments ?? 0)],
      ["Locked Applications", String(stats.lockedApplications ?? 0)],
      ["Total Applications Disbursed", String(stats.totalApplicationsDisbursed ?? 0)],
      ["Upcoming interviews", String(stats.upcomingInterviews ?? 0)],
      ["Mentorship logs due", String(stats.mentorshipLogs ?? 0)],
      ["Assigned regions", (stats.assignedRegions ?? []).join("; ")],
      ["Start date", toYMDOptional(dateRange.startDate) ?? "All"],
      ["End date", toYMDOptional(dateRange.endDate) ?? "All"],
    ];

    const csv = rows
      .map((r) => r.map((x) => `"${String(x).replaceAll(`"`, `""`)}"`).join(","))
      .join("\n");

    saveAs(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
      `ddo_dashboard_${toYMDOptional(dateRange.startDate) ?? "all"}_${toYMDOptional(dateRange.endDate) ?? "all"}.csv`
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="p-6 space-y-5">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">Application Review Dashboard</h1>
        <div className="text-sm text-muted-foreground">
          Tenant: <span className="font-medium text-gray-900">{slug}</span>
        </div>
      </div>

      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 text-sm text-blue-950">
          <div className="font-semibold">What should I do next?</div>
          <p className="mt-1">{nextAction}</p>
        </CardContent>
      </Card>

      <div className="grid gap-3 rounded-md border bg-white p-4 md:grid-cols-5">
        <label className="flex flex-col text-sm">
          <span className="font-medium text-muted-foreground">From</span>
          <input
            type="date"
            value={toYMDOptional(dateRange.startDate) ?? ""}
            onChange={(event) =>
              setDateRange((current) => ({
                ...current,
                startDate: parseYMD(event.target.value),
              }))
            }
            className="rounded border px-3 py-2"
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className="font-medium text-muted-foreground">To</span>
          <input
            type="date"
            value={toYMDOptional(dateRange.endDate) ?? ""}
            onChange={(event) =>
              setDateRange((current) => ({
                ...current,
                endDate: parseYMD(event.target.value),
              }))
            }
            className="rounded border px-3 py-2"
          />
        </label>
        <Input
          value={programFilter}
          onChange={(event) => setProgramFilter(event.target.value)}
          placeholder="Program code"
        />
        <Input
          value={academicYearFilter}
          onChange={(event) => setAcademicYearFilter(event.target.value)}
          placeholder="Academic year"
          inputMode="numeric"
        />
        <Input
          value={applicantNameFilter}
          onChange={(event) => setApplicantNameFilter(event.target.value)}
          placeholder="Applicant name"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <Button variant="secondary" onClick={exportCSV} disabled={!stats}>
          Export summary
        </Button>
        <Button variant="outline" onClick={() => openQueue({})}>
          Open full worklist
        </Button>
      </div>

      {error ? <p className="text-red-600">{error}</p> : null}

      {!stats && !error ? (
        <div className="grid md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-[140px] w-full rounded-xl" />
          ))}
        </div>
      ) : null}

      {/* Truthful empty state */}
      {stats && isTrulyEmpty ? (
        <div className="border rounded-xl p-6 bg-white space-y-3">
          <div className="text-lg font-semibold">No work assigned yet</div>
          <div className="text-sm text-muted-foreground">
            There are no submissions currently assigned to you for this tenant and date range.
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openQueue({})}>
              Open submissions
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                setDateRange({
                  startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
                  endDate: new Date(),
                })
              }
            >
              Expand to last 90 days
            </Button>
          </div>
        </div>
      ) : null}

      {/* Action tiles */}
      {stats && !isTrulyEmpty ? (
        <div className="grid md:grid-cols-3 gap-4">
          {quickFilters.map((q) => (
            <ClickableStatCard
              key={q.label}
              label={q.label}
              value={q.value}
              help={q.help}
              onClick={() => openQueue(q.params)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default DDODashboard;
